import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WifiOff, RefreshCw, AlertTriangle, CloudUpload } from "lucide-react";
import { outboxAll, conflictsAll } from "@/lib/offline";
import { isNetOnline } from "@/lib/connectivity";
import { toast } from "sonner";

export const OfflineBanner = () => {
  const [online, setOnline] = useState(isNetOnline());
  const [queued, setQueued] = useState(0);
  const [conflicts, setConflicts] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = () => {
    outboxAll().then((l) => setQueued(l.length)).catch(() => {});
    conflictsAll().then((l) => setConflicts(l.length)).catch(() => {});
  };

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    const onNet = (e) => setOnline(!!e.detail?.online);
    const onSync = (e) => {
      const t = e.detail?.type;
      if (t === "syncing") setSyncing(true);
      if (t === "done") {
        setSyncing(false);
        refresh();
        const { synced = 0, conflicted = 0 } = e.detail || {};
        if (synced) toast.success(`${synced} offline change${synced > 1 ? "s" : ""} synced`);
        if (conflicted) toast.warning(`${conflicted} change${conflicted > 1 ? "s" : ""} need review in Sync Center`);
      }
      if (t === "queued" || t === "idle") refresh();
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("net-status", onNet);
    window.addEventListener("offline-sync", onSync);
    refresh();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); window.removeEventListener("net-status", onNet); window.removeEventListener("offline-sync", onSync); };
  }, []);

  if (online && !queued && !conflicts && !syncing) return null;
  return (
    <div className={`px-4 py-2 text-xs font-medium flex items-center gap-2 flex-wrap ${!online ? "bg-amber-500 text-white" : conflicts ? "bg-rose-600 text-white" : "bg-blue-600 text-white"}`}
         data-testid="offline-banner">
      {!online ? (<><WifiOff size={13} /> Offline — you can keep working; changes are saved locally and will sync automatically.</>)
        : syncing ? (<><RefreshCw size={13} className="animate-spin" /> Syncing offline changes…</>)
        : conflicts ? (<><AlertTriangle size={13} /> {conflicts} change{conflicts > 1 ? "s" : ""} conflicted during sync.</>)
        : (<><CloudUpload size={13} /> {queued} change{queued > 1 ? "s" : ""} waiting to sync.</>)}
      {(queued > 0 || conflicts > 0) && (
        <Link to="/sync" className="underline font-semibold ml-1" data-testid="offline-banner-link">Open Sync Center</Link>
      )}
    </div>
  );
};
